import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { LoadingState } from '@/components/ui/LoadingState';

interface Props {
  capability: string;
  children: React.ReactNode;
  /** What to show when the user lacks the capability. Defaults to a redirect to /dashboard. */
  fallback?: React.ReactNode;
}

/**
 * Gate a route or UI block on whether the logged-in user has a given
 * capability. Capabilities are seeded from role defaults at login and
 * then refined by GET /auth/me/capabilities (which merges role defaults
 * with per-user overrides from the user_permissions table). See
 * AuthContext for the full lifecycle.
 *
 * Important: V4.2 redirected to /dashboard immediately if the capability
 * was missing, even when the authoritative set hadn't loaded yet — that
 * caused non-admins to be bounced back to the dashboard the first time
 * /auth/me/capabilities was slow. We now wait for either a confirmed
 * load OR a non-empty role-defaults seed before deciding.
 *
 * Use this for features the Permissions admin matrix can toggle. For
 * always-admin features, use <RequireRole> instead.
 */
export function RequireCapability({ capability, children, fallback }: Props) {
  const { hasCapability, isBooting, capabilitiesLoaded, user } = useAuth();
  if (isBooting) return null;
  // Fast path: the role-default seed OR the server set already grants it → allow.
  if (hasCapability(capability)) {
    return <>{children}</>;
  }
  // Not granted by the seed — but a PER-USER grant (from the Permissions matrix)
  // may still arrive once /auth/me/capabilities resolves. The seed only carries
  // role defaults, so it is NOT authoritative for grantees. Wait (spinner) until
  // the authoritative set has loaded before bouncing — otherwise an admin-granted
  // Agent/Communicator is wrongly kicked to /dashboard during the load window.
  if (user && !capabilitiesLoaded) {
    return <LoadingState label="Loading permissions..." />;
  }
  return <>{fallback ?? <Navigate to="/dashboard" replace />}</>;
}
